import {Injectable, Injector} from "@angular/core";
import {
    collection,
    component,
    EntityData,
    EntityModel,
    FedeltaImageData,
    field,
    identifier,
    localDateField
} from "@fedelta-pos/data-client";
import {DealData} from "../deal/deal-entity.model";
import {AddressData} from "../customer-info/address.data";
import {MenuSellableCategoryLookupData, MenuSellableData} from "../menu/menu-entity.model";
import {OrderType} from "../order/order";

export class MemberSummaryContactData extends EntityData<MemberSummaryContactData> {
    @identifier id: string;
    @field firstName: string;
    @field lastName: string;
    @field email: string;
    @field mobilePhone: string;
    @field dob: Date;
    @field gender: number;
    @field deliveryInstructions: string;
    @component homeAddress: AddressData;
}

export class AppPromoCategoryData extends EntityData<AppPromoCategoryData> {
    @identifier categoryId: number;
    @field name: string;
}

export class AppPromoSellableData extends EntityData<AppPromoSellableData> {
    @identifier sellableId: number;
    @field name: string;
}

export class AppPromoPromotedSellableData extends EntityData<AppPromoPromotedSellableData> {
    @component sellable: AppPromoSellableData;
    @component productImage: FedeltaImageData;
    @field promoteIfOrdered: boolean = false;
}

export enum AppPromoAction {
    ORDER_PRODUCT = <any>"ORDER_PRODUCT",
    SHOW_PRODUCTS = <any>"SHOW_PRODUCTS"
}

export class AppPromoData extends EntityData<AppPromoData> {
    @identifier id: string;
    @field name: string;
    @localDateField startDate: Date;
    @localDateField endDate: Date;
    @field appId: number;
    @field scheduleRequired: boolean;
    @field orderAhead: boolean;
    @field orderDelivery: boolean;
    @field orderAtTable: boolean;
    @field triggered: boolean = true;
    @component triggerCategory: AppPromoCategoryData;
    @collection(AppPromoSellableData) triggerSellables: AppPromoSellableData[];
    @field showAppPromoCard: boolean;
    @field showPopupWhenOrdered: boolean;
    @field showPopupOnCheckout: boolean;
    @field featureOnAppMenu: boolean;
    @field suppressPromoCardIfAnyOrdered: boolean = true;
    @field suppressPromoCardIfOrdered: boolean = true;
    @field suppressPromoCardProducts: boolean = true;
    @field suppressPopupIfOrdered: boolean = true;
    @field suppressPopupProducts: boolean = true;
    @field popupLimit: number = 2;
    @field featurePromotedMenuItems: boolean = false;
    @field menuItemFeatureTag: string;
    @field displayPromotedMenuCategory: boolean = false;
    @field suppressCategoryIfOrdered: boolean = false;
    @field suppressCategoryProducts: boolean = false;
    @field itemImagesInPromoOnly: boolean = false;
    @component image: FedeltaImageData;
    @field imageAutoCrop: boolean;
    @component categoryImage: FedeltaImageData;
    @field appText1: string;
    @field appText2: string;
    @field categoryTitle: string;
    @field categoryDescription: string;
    @field showDescriptionOnCategory: boolean;
    @field showImageOnCategory: boolean;
    @field showTitleOnPromoCard: boolean;
    @field showMessageOnPromoCard: boolean = true;
    @field showDetailsOnPromoCard: boolean = true;
    @field showActionButtonOnPromoCard: boolean = true;
    @field promoCardAction: AppPromoAction;
    @field showMessageOnPopup: boolean = true;
    @field showDetailsOnPopup: boolean = true;
    @field showDescriptionOnPopup: boolean;
    @field showActionButtonOnPopup: boolean = true;
    @field popupAction: AppPromoAction;
    @collection(AppPromoPromotedSellableData) promotedSellables: AppPromoPromotedSellableData[];

    public isTriggeredBySellable(sellable: MenuSellableData): boolean {
        return this.triggered &&
            ((this.triggerCategory.categoryId &&
                    (sellable.primaryCategory.categoryId === this.triggerCategory.categoryId ||
                        sellable.primaryCategory.ancestors.some((ancestor: MenuSellableCategoryLookupData) => ancestor.ancestorId === this.triggerCategory.categoryId))) ||
                this.triggerSellables.some((triggerSellable: AppPromoSellableData) => triggerSellable.sellableId === sellable.sellableId));
    }

    // Promos are only available if they specify the order type.
    // If we don't know the order type, only promos specifying all order types are available.
    public isAvailableForOrderType(orderType: OrderType): boolean {
        return (!orderType && (this.orderAhead && this.orderDelivery && this.orderAtTable)) ||
            (orderType === OrderType.ORDER_AHEAD && this.orderAhead) ||
            (orderType === OrderType.ORDER_DELIVERY && this.orderDelivery) ||
            (orderType === OrderType.ORDER_AT_TABLE && this.orderAtTable);
    }
}

export class MemberRecentSaleData extends EntityData<MemberRecentSaleData> {
    @field orderReceiptId: string;
    @field orderLookupCode: string;
}

export enum LoyaltyCardType {
    STAMPS = <any>"Stamps",
    POINTS = <any>"Points"
}

export class LoyaltyCardData extends EntityData<LoyaltyCardData> {
    @identifier id: number;
    @field integratedLoyaltyConfigId: number;
    @field providerName: string;
    @field name: string;
    @field cardType: LoyaltyCardType;
    @field cardName: string;
    @field pointsRequiredToDisplay: number;
    @field pointsRequiredToRedeem: number;
    @field itemDescription: string;
    @field goalDescription: string;
    @field goal: number;
    @field purchaseCount: number;
    @component image: FedeltaImageData;
    @field theme: string;
}

export class MemberSummaryMembershipTypeData extends EntityData<MemberSummaryMembershipTypeData> {
    @identifier membershipTypeId: number;
    @field name: string;
}

export class MemberSummaryData extends EntityData<MemberSummaryData> {
    @identifier memberId: string;
    @field memberNo: string;
    @field memberCardNo: string;
    @component membershipType: MemberSummaryMembershipTypeData;
    @field integratedLoyaltyId: string;
    @field integratedCardNo: string;
    @field pointsBalance: number;
    @field allowProfileEdit: boolean;
    @field allowPasswordChange: boolean;
    @component contact: MemberSummaryContactData;
    @collection(DealData) deals: DealData[];
    @field anySelectByDealCodeInApp: boolean;
    @field anySelectByNonNumericDealCodeInApp: boolean;
    @collection(AppPromoData) promos: AppPromoData[];
    @collection(MemberRecentSaleData) recentSales: MemberRecentSaleData[];
    @collection(LoyaltyCardData) loyaltyCards: LoyaltyCardData[];
}

@Injectable()
export class MemberSummaryEntityModel extends EntityModel<MemberSummaryData> {
    constructor(injector: Injector) {
        console.log("--- MemberSummaryEntityModel");

        super(injector, MemberSummaryData);
    }
}



// WEBPACK FOOTER //
// ./src/app/identity/member-summary-entity.model.ts